import React from "react"
import styled from "styled-components"
import { theme } from "@/common/Mixin"
import { ProductNames, SolutionNames } from "@/common/data/Products"

import User360Dark from "@/common/assets/image/products/icons/User360Dark.svg"
import WalletBuilderDark from "@/common/assets/image/products/icons/WalletBuilderDark.svg"
import WalletSDKDark from "@/common/assets/image/products/icons/WalletSDKDark.svg"
import WalletAPIDark from "@/common/assets/image/products/icons/WalletAPIDark.svg"
import WalletAPPDark from "@/common/assets/image/products/icons/WalletAPPDark.svg"
import WalletExtensionDark from "@/common/assets/image/products/icons/WalletExtensionDark.svg"
import AssetProDark from "@/common/assets/image/products/icons/AssetProDark.svg"
import ComplianceProDark from "@/common/assets/image/products/icons/ComplianceProDark.svg"
import ComplianceLiteDark from "@/common/assets/image/products/icons/ComplianceLiteDark.svg"
import ComplianceAPIDark from "@/common/assets/image/products/icons/ComplianceAPIDark.svg"
import ComplyFlowDark from "@/common/assets/image/products/icons/ComplyFlowDark.svg"
import NFTAirdropDark from "@/common/assets/image/products/icons/NFTAirdropDark.svg"
import NFTBoostDark from "@/common/assets/image/products/icons/NFTBoostDark.svg"
import OAuthSDKDark from "@/common/assets/image/products/icons/OAuthSDKDark.svg"
import TransferDark from "@/common/assets/image/products/icons/TransferDark.svg"

import NextImg from "../NextImg"
import Link from "next/link"
import { useTranslation } from "next-i18next"

const Fade = require("react-reveal/Fade")

interface ProductCardData {
  icon?: string
  title: string
  description: string
  url: string
  tag?: string
}

const getDisplayData = (
  t: any,
  type: ProductNames | SolutionNames
): ProductCardData => {
  switch (type) {
    case SolutionNames.GameFi:
      return {
        title: t("Solution.gamefi.title"),
        description: t("Solution.gamefi.description"),
        url: "/solutions/gamefi"
      }
    case SolutionNames.Defi:
      return {
        title: t("Solution.defi.title"),
        description: t("Solution.defi.description"),
        url: "/solutions/defi"
      }
    case ProductNames.User360:
      return {
        icon: User360Dark.src,
        title: t("Product.user360.title"),
        description: t("Product.user360.description"),
        url: "/products/user360",
        tag: "AI"
      }
    case ProductNames.WalletBuilder:
      return {
        icon: WalletBuilderDark.src,
        title: t("Product.walletBuilder.title"),
        description: t("Product.walletBuilder.description"),
        url: "/products/wallet-builder",
        tag: "AI"
      }
    case ProductNames.WalletSDK:
      return {
        icon: WalletSDKDark.src,
        title: t("Product.walletSDK.title"),
        description: t("Product.walletSDK.description"),
        url: "/products/wallet-sdk"
      }
    case ProductNames.WalletAPI:
      return {
        icon: WalletAPIDark.src,
        title: t("Product.walletAPI.title"),
        description: t("Product.walletAPI.description"),
        url: "/products/wallet-api"
      }
    case ProductNames.WalletApplication:
      return {
        icon: WalletAPPDark.src,
        title: t("Product.walletApplication.title"),
        description: t("Product.walletApplication.description"),
        url: "/products/wallet"
      }
    case ProductNames.WalletExtension:
      return {
        icon: WalletExtensionDark.src,
        title: t("Product.walletExtension.title"),
        description: t("Product.walletExtension.description"),
        url: "/products/wallet-extension"
      }
    case ProductNames.AssetPro:
      return {
        icon: AssetProDark.src,
        title: t("Product.assetPro.title"),
        description: t("Product.assetPro.description"),
        url: "/products/asset-pro"
      }
    case ProductNames.CompliancePro:
      return {
        icon: ComplianceProDark.src,
        title: t("Product.compliancePro.title"),
        description: t("Product.compliancePro.description"),
        url: "/products/compliance-pro",
        tag: "AI"
      }
    case ProductNames.ComplianceLite:
      return {
        icon: ComplianceLiteDark.src,
        title: t("Product.complianceLite.title"),
        description: t("Product.complianceLite.description"),
        url: "/products/compliance-lite"
      }
    case ProductNames.ComplianceAPI:
      return {
        icon: ComplianceAPIDark.src,
        title: t("Product.complianceAPI.title"),
        description: t("Product.complianceAPI.description"),
        url: "/products/compliance-api"
      }
    case ProductNames.ComplyFlow:
      return {
        icon: ComplyFlowDark.src,
        title: t("Product.complyFlow.title"),
        description: t("Product.complyFlow.description"),
        url: "/products/comply-flow"
      }
    case ProductNames.NFTAirdrop:
      return {
        icon: NFTAirdropDark.src,
        title: t("Product.nftAirdrop.title"),
        description: t("Product.nftAirdrop.description"),
        url: "/products/nft-airdrop",
        tag: "AI"
      }
    case ProductNames.NFTBoost:
      return {
        icon: NFTBoostDark.src,
        title: t("Product.nftBoost.title"),
        description: t("Product.nftBoost.description"),
        url: "/products/nft-boost"
      }
    case ProductNames.OAuthSDK:
      return {
        icon: OAuthSDKDark.src,
        title: t("Product.oauthSDK.title"),
        description: t("Product.oauthSDK.description"),
        url: "/products/oauth-sdk"
      }
    case ProductNames.Transfer:
      return {
        icon: TransferDark.src,
        title: t("Product.transfer.title"),
        description: t("Product.transfer.description"),
        url: "/products/transfer"
      }
    default:
      return {
        icon: WalletBuilderDark.src,
        title: t("Product.oauthSDK.title"),
        description: t("Product.oauthSDK.description"),
        url: "/products/oauth-sdk"
      }
  }
}

interface ProductCardProps {
  productName: ProductNames
  className?: string
  selected?: boolean
}

const ProductCard: React.FC<ProductCardProps> = ({
  productName,
  className,
  selected
}) => {
  const { t } = useTranslation(["common"])
  const { icon, title, description, url, tag } = getDisplayData(t, productName)
  return (
    <CardWrapper
      href={url}
      target="_blank"
      selected={selected ?? false}
      className={className}
    >
      {tag && <Tag className="body">{tag}</Tag>}
      {icon && (
        <IconWrapper>
          <NextImg src={icon} alt="Product Icon" />
        </IconWrapper>
      )}
      <Fade bottom>
        <TitleWrapper className="title">{title}</TitleWrapper>
      </Fade>
      <TextWrapper className="body2">{description}</TextWrapper>
    </CardWrapper>
  )
}

const Tag = styled.span`
  display: flex;
  padding: 4px 16px;
  justify-content: center;
  align-items: flex-start;
  position: absolute;
  top: 0px;
  right: 16px;
  background-color: ${theme.color.primary};
  color: ${theme.color.background};
  z-index: 1;
`

// @ts-ignore
const CardWrapper = styled(Link)<{ selected: boolean }>`
  position: relative;
  display: flex;
  padding: 32px 32px 24px 32px;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
  background-color: ${({ selected }) =>
    selected ? theme.color.primary : theme.color.secondary};
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    background-color: ${theme.color.transparentHover};

    .title {
      color: ${theme.color.primary};
    }
  }
  &:active {
    background-color: ${theme.color.primaryDark};
  }
  @media (${theme.lg}) {
    max-width: 100%;
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`

const TitleWrapper = styled.h4`
  color: ${theme.color.textContrast};
`

const TextWrapper = styled.p`
  color: ${theme.color.textLightGray};
`

export default ProductCard
